/**
 * @module immersive/Celebration
 * @description The Shot celebration (vision §2.3, §12): Cupid pops in, the
 * arrow flies, hearts burst; Twin arrows play doubled. Plays on the DONOR'S
 * OWN screen only and says "added to your Quiver" (never "gift sent"),
 * because nothing is public until Neon confirms payment (vision §10.2).
 *
 * At most 6 arrows animate; beyond that the largest play with a ×N badge
 * (vision §6.3). Under reduced motion the same card appears without movement.
 */
import { useEffect, type CSSProperties, type JSX } from "react";
import { arrowLabel, groupArrows, isTwin, type LegendArrow } from "../domain/arrows";
import { formatUsd } from "../lib/format";
import { ArrowGlyph } from "./ArrowFace";
import { useStage, type Celebration as CelebrationData } from "./StageContext";

/** Max individually animated arrows (vision §6.3). */
const MAX_ANIMATED: number = 6;
/** On-screen time per celebration. */
const SHOW_MS: number = 2600;

/** The overlay; renders nothing when idle. */
export function Celebration(): JSX.Element | null {
    const { celebration, endCelebration } = useStage();

    useEffect(() => {
        if (celebration === null) return;
        const id: number = window.setTimeout(endCelebration, SHOW_MS);
        return () => window.clearTimeout(id);
    }, [celebration, endCelebration]);

    if (celebration === null) return null;
    const c: CelebrationData = celebration;
    const total: number = c.arrows.reduce((s: number, a: LegendArrow) => s + a.faceUsd, 0);
    const animated: LegendArrow[] = c.arrows.slice(0, MAX_ANIMATED);
    const extra: number = c.arrows.length - animated.length;
    const twin: boolean = c.arrows.some(isTwin);
    const headline: string = c.arrows.length === 1 ? arrowLabel(c.arrows[0] as LegendArrow) : `${formatUsd(total)} in ${c.arrows.length} Legend Arrows`;

    return (
        <div className="celebration" role="status" key={c.id} onClick={endCelebration}>
            <div className={`celebration__card${twin ? " celebration__card--twin" : ""}`}>
                <img className="celebration__cupid" src="cupid/cupid-cheer.webp" alt="" width={220} height={221} />
                <div className="celebration__flight" aria-hidden="true">
                    {animated.map((a: LegendArrow, i: number) => (
                        <span key={i} className="celebration__arrow" style={{ "--i": i } as CSSProperties}><ArrowGlyph size={44} twin={isTwin(a)} /></span>
                    ))}
                    {Array.from({ length: 10 }, (_, i: number) => (
                        <span key={`h${i}`} className="celebration__heart" style={{ "--h": i } as CSSProperties}>♥</span>
                    ))}
                </div>
                <p className="celebration__label">{headline}{extra > 0 && <span className="celebration__more"> ×{extra} more</span>}</p>
                <p className="celebration__sub">Added to your Quiver for {c.causeTitle}</p>
                {c.arrows.length > 1 && (
                    <p className="celebration__mix">{groupArrows(c.arrows).map(([a, n]) => `${a.name}${n > 1 ? ` ×${n}` : ""}`).join(" · ")}</p>
                )}
            </div>
        </div>
    );
}
